import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { Briefcase, WalletMoney, Profile, Trash, InfoCircle } from "iconsax-react";
import {
  Input,
  Button,
  Select,
  DatePicker,
  Prompt,
  PromptForm,
  ModalFormSplit,
  SearchableSelect,
  Skeleton,
  CitySelect,
  RadioGroup,
  RadioButton,
  Tooltip,
  Toast,
} from "@/shared/ui";
import { FileUploader } from "@/shared/components";
import { formatMoneyKzt, toNumber, FINANCE_PAGE_ROUTE } from "@/shared/utils";
import { useAuth } from "@/shared/hooks";
import { PERMISSIONS } from "@/shared/consts/permissions";
import { useGetAvailableExternalIncomesQuery } from "@/features/accounting/income";
import type { Project } from "../types";
import { useProjectForm } from "../hooks";
import { PROJECT_CATEGORIES, CATEGORY_MAP } from "../utils";
import { PLANNED_FIELDS, PLANNED_LABELS } from "../utils/plannedFields";
import type { PlannedField } from "../utils/plannedFields";
import PartnerInput from "./PartnerInput";
import IncomeSourcesTable from "./IncomeSourcesTable";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  isRefetching: boolean;
  project: Project | null;
  mode: "create" | "edit";
  onRequestRemoval?: () => void;
}

interface PlannedInputsProps {
  watch: ReturnType<typeof useProjectForm>["watch"];
  setValue: ReturnType<typeof useProjectForm>["setValue"];
  errors: ReturnType<typeof useProjectForm>["errors"];
  t: (key: string) => string;
  locale: "ru" | "kk" | "en";
  readOnly?: boolean;
}

function PlannedInputs({ watch, setValue, errors, t, locale, readOnly }: PlannedInputsProps) {
  const total = PLANNED_FIELDS.reduce((acc, field) => acc + toNumber(watch(field) || "0"), 0);

  function handleChange(field: PlannedField, value: string) {
    const cleaned = value.replace(/[^0-9.]/g, "");
    setValue(field, cleaned, { shouldValidate: true });
  }

  return (
    <>
      <div className="grid grid-cols-2 gap-3">
        {PLANNED_FIELDS.map((field) => (
          <Input
            key={field}
            label={t(PLANNED_LABELS[field])}
            placeholder="0.00"
            value={watch(field) ?? ""}
            onChange={(e) => handleChange(field, e.target.value)}
            error={errors[field]?.message ? t(errors[field]!.message as string) : undefined}
            disabled={readOnly}
          />
        ))}
      </div>
      <div className="mt-3 flex items-center justify-between p-3 radius-md surface-secondary-fill">
        <span className="text-body-bold-sm content-base-primary">{t("projectForm.fields.budgetTotal")}</span>
        <span className="text-body-bold-md content-base-primary">{formatMoneyKzt(total, locale, false)}</span>
      </div>
    </>
  );
}

export default function ProjectFormModal({ isOpen, onClose, onSuccess, isRefetching, project, mode, onRequestRemoval }: Props) {
  const { t, i18n } = useTranslation("ProjectsPage");
  const locale = (i18n.language as "ru" | "kk" | "en") || "ru";
  const {
    register,
    watch,
    setValue,
    errors,
    isValid,
    isSubmitting,
    errorPrompt,
    handleCloseErrorPrompt,
    handleSubmit,
    members,
    isMembersLoading,
    isMembersFetching,
    availableOrgMembers,
    addingMember,
    removingMemberId,
    confirmRemoveMemberId,
    handleAddMember,
    requestRemoveMember,
    cancelRemoveMember,
    confirmRemoveMember,
    existingPictureUrl,
    handleRemoveExistingPicture,
  } = useProjectForm({
    project,
    onSuccess,
    mode,
  });

  const { hasPermission } = useAuth();
  const navigate = useNavigate();

  const isInternal = mode === "create" ? watch("is_internal") === true : (project?.is_internal ?? false);
  const isExternalCreate = mode === "create" && !isInternal;

  const { data: availableIncomes } = useGetAvailableExternalIncomesQuery(undefined, {
    skip: !isExternalCreate,
  });
  const noAvailableSources = isExternalCreate && availableIncomes !== undefined && availableIncomes.length === 0;

  const isLoading = isSubmitting || isRefetching;
  const isSubmitDisabled = isLoading || noAvailableSources || (mode === "create" && !isValid);
  const showPlanned = !isInternal;
  const canRedistribute =
    mode === "edit" &&
    !!project &&
    !project.is_internal &&
    (project.status === "active" || project.status === "planned") &&
    hasPermission(PERMISSIONS.PROJECTS.EDIT_PROJECT);

  function handleRedistribute() {
    if (!project) return;
    navigate(`${FINANCE_PAGE_ROUTE}?redistribute=${project.id}`);
  }

  const statusOptions = [
    { label: t("status.planned"), value: "planned" as const },
    { label: t("status.active"), value: "active" as const },
    { label: t("status.paused"), value: "paused" as const },
    { label: t("status.closed"), value: "closed" as const },
  ];

  const categoryOptions = PROJECT_CATEGORIES.map((cat) => ({
    label: t(CATEGORY_MAP[cat].label),
    description: t(CATEGORY_MAP[cat].description),
    icon: CATEGORY_MAP[cat].icon,
    value: cat,
  }));

  if (!isOpen) return null;

  const leftPanel = (
    <>
      {mode === "create" && (
        <div className="flex flex-col gap-2">
          <p className="text-body-bold-sm content-base-primary flex items-center gap-1.5">
            {t("projectForm.fields.projectType")}
            <Tooltip
              width="w-80"
              content={
                <p className="text-label-xs content-action-neutral leading-relaxed">
                  {t("projectForm.projectTypeTooltip")}
                </p>
              }>
              <InfoCircle
                size={16}
                color="currentColor"
                className="content-base-low cursor-pointer hover:opacity-80"
              />
            </Tooltip>
          </p>
          <RadioGroup
            name="is_internal"
            value={watch("is_internal") ? "internal" : "external"}
            onChange={(val) => setValue("is_internal", val === "internal", { shouldValidate: true })}
            wrapperClassName="grid grid-cols-2 gap-3">
            <RadioButton value="external" label={t("projectForm.typeExternal")} />
            <RadioButton value="internal" label={t("projectForm.typeInternal")} />
          </RadioGroup>
        </div>
      )}
      <Input
        label={t("projectForm.fields.name")}
        placeholder={t("projectForm.placeholders.name")}
        {...register("name")}
        error={errors.name?.message ? t(errors.name.message) : undefined}
      />

      <Input
        isTextarea
        label={t("projectForm.fields.description")}
        placeholder={t("projectForm.placeholders.description")}
        {...register("description")}
        error={errors.description?.message ? t(errors.description.message) : undefined}
      />

      <Select
        label={t("projectForm.fields.status")}
        options={statusOptions}
        value={watch("status")}
        onChange={(v) => setValue("status", v ?? "planned", { shouldValidate: true })}
      />

      <SearchableSelect
        label={t("projectForm.fields.category")}
        placeholder={t("projectForm.placeholders.category")}
        searchPlaceholder={t("filterPanel.searchPlaceholder")}
        options={categoryOptions}
        value={categoryOptions.find((o) => o.value === watch("category")) ?? null}
        onChange={(option) => setValue("category", option?.value ?? "", { shouldValidate: true })}
        searchKeys={["label", "description"]}
        displayKey="label"
        getOptionLabel={(option) => option.label}
        isOptionEqual={(a, b) => a.value === b.value}
        error={errors.category?.message ? t(errors.category.message) : undefined}
        renderOption={(option) => (
          <div className="flex items-center gap-2.5 w-full">
            <span className="shrink-0 w-5 h-5 flex items-center justify-center">{option.icon}</span>
            <div className="flex flex-col min-w-0">
              <span className="text-label-sm truncate">{option.label}</span>
              <span className="text-body-regular-xs content-base-secondary truncate">{option.description}</span>
            </div>
          </div>
        )}
      />

      <CitySelect
        label={t("projectForm.fields.region")}
        placeholder={t("projectForm.placeholders.region")}
        dataSource="regions"
        value={watch("region_id") ?? undefined}
        onChange={(v) => setValue("region_id", v ?? null, { shouldValidate: true })}
        error={errors.region_id?.message ? t(errors.region_id.message) : undefined}
      />

      <div className="grid grid-cols-2 gap-4">
        <DatePicker
          mode="single"
          label={t("projectForm.fields.startDate")}
          locale={locale}
          value={watch("start_date") ? new Date(watch("start_date")!) : null}
          onChange={(date) =>
            setValue("start_date", date ? date.toISOString().split("T")[0] : "", {
              shouldValidate: true,
            })
          }
          error={errors.start_date?.message ? t(errors.start_date.message) : undefined}
        />

        <DatePicker
          mode="single"
          label={t("projectForm.fields.endDate")}
          locale={locale}
          value={watch("end_date") ? new Date(watch("end_date")!) : null}
          onChange={(date) =>
            setValue("end_date", date ? date.toISOString().split("T")[0] : "", {
              shouldValidate: true,
            })
          }
          error={errors.end_date?.message ? t(errors.end_date.message) : undefined}
        />
      </div>

      <Input
        label={t("projectForm.fields.beneficiariesCoverage")}
        placeholder={t("projectForm.placeholders.beneficiariesCoverage")}
        {...register("beneficiaries_coverage")}
        error={errors.beneficiaries_coverage?.message ? t(errors.beneficiaries_coverage.message) : undefined}
        onlyNumber
      />
    </>
  );

  const rightPanel = (
    <div className="flex flex-col gap-3 py-3">
      <div className="surface-base-fill rounded-xl p-4 flex flex-col gap-3">
        <FileUploader
          label={t("projectForm.fields.projectPicture")}
          maxSizeMB={10}
          accept=".jpg,.jpeg,.png,.webp,.svg"
          value={watch("project_picture") ?? null}
          existingFileUrl={existingPictureUrl}
          onChange={(file) => setValue("project_picture", file, { shouldValidate: true })}
          onRemoveExisting={handleRemoveExistingPicture}
          namespace="ProjectsPage"
          formatsText={t("projectForm.pictureFormats")}
        />

        {isExternalCreate && (
          <div>
            <p className="text-body-bold-sm content-base-primary py-2 mb-2">{t("projectForm.fields.sources")}</p>
            <IncomeSourcesTable
              sources={watch("sources") ?? []}
              onChange={(sources) => setValue("sources", sources, { shouldValidate: true })}
              error={errors.sources?.message ? t(errors.sources.message as string) : undefined}
            />
            {noAvailableSources && (
              <div className="mt-2">
                <Toast color="notice" text={t("projectForm.sourcesEmptyHint")} closable={false} autoClose={false} isFullWidth />
              </div>
            )}
          </div>
        )}

        {showPlanned && (
          <div>
            <div className="flex items-center justify-between gap-3 py-2 mb-2">
              <p className="text-body-bold-sm content-base-primary">{t("projectForm.fields.plannedAmounts")}</p>
              {canRedistribute && (
                <Button variant="tertiary" size="md" type="button" onClick={handleRedistribute}>
                  {t("projectForm.redistribute.button")}
                </Button>
              )}
            </div>
            <PlannedInputs
              watch={watch}
              setValue={setValue}
              errors={errors}
              t={t}
              locale={locale}
              readOnly={mode === "edit"}
            />
          </div>
        )}

        <PartnerInput
          label={t("projectForm.fields.partners")}
          addLabel={t("projectForm.actions.addPartner")}
          placeholder={t("projectForm.placeholders.partnerName")}
          externalLabel={t("projectForm.fields.isExternal")}
          value={watch("partners") || []}
          onChange={(partners) => setValue("partners", partners, { shouldValidate: true })}
        />
      </div>

      {mode === "edit" && project && (
        <div className="surface-base-fill rounded-xl p-4 flex flex-col gap-3">
          <div className="border-b surface-base-stroke pb-3">
            <h5 className="text-body-bold-md content-base-primary">
              {t("members.title")} {members.length > 0 && `(${members.length})`}
            </h5>
          </div>

          <SearchableSelect
            label={t("members.addLabel")}
            placeholder={t("members.addPlaceholder")}
            searchPlaceholder={t("members.searchPlaceholder")}
            options={availableOrgMembers}
            value={null}
            onChange={(member) => {
              if (member) handleAddMember(member.id);
            }}
            searchKeys={["name"]}
            displayKey="name"
            disabled={addingMember}
          />

          {isMembersLoading ? (
            <div className="flex flex-col gap-2">
              <Skeleton className="h-14 w-full rounded-xl" />
              <Skeleton className="h-14 w-full rounded-xl" />
            </div>
          ) : members.length === 0 ? (
            <p className="text-body-regular-sm content-base-secondary py-2">{t("members.empty")}</p>
          ) : (
            <div className="flex flex-col gap-2">
              {members.map((member) => (
                <div
                  key={member.id}
                  className={`flex items-center gap-3 p-3 rounded-xl border surface-base-stroke${removingMemberId === member.id ? " opacity-40" : ""}`}>
                  <div className="w-8 h-8 rounded-full flex items-center justify-center surface-component-fill content-action-brand">
                    <Profile size={16} color="currentColor" />
                  </div>
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <span className="text-body-bold-sm content-base-primary truncate">{member.name}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => requestRemoveMember(member.id)}
                    disabled={removingMemberId !== null}
                    className={`w-7 h-7 flex items-center justify-center rounded-md cursor-pointer content-action-negative hover:opacity-80 shrink-0${removingMemberId !== null ? " opacity-50 cursor-not-allowed" : ""}`}>
                    <Trash size={14} color="currentColor" />
                  </button>
                </div>
              ))}
              {addingMember && isMembersFetching && <Skeleton className="h-14 w-full rounded-xl" />}
            </div>
          )}
        </div>
      )}
    </div>
  );

  const canRemove = mode === "edit" && project !== null && !project.is_default && onRequestRemoval !== undefined;

  const footer = canRemove ? (
    <div className="border-t surface-base-stroke pt-3 flex flex-col gap-3">
      <Button variant="primary" type="submit" className="w-full" disabled={isSubmitDisabled}>
        {isLoading ? t("projectForm.actions.submitting") : t("projectForm.actions.save")}
      </Button>
      <div className="grid grid-cols-[2fr_3fr] gap-3">
        <Button variant="secondary" onClick={onClose} type="button" disabled={isLoading}>
          {t("projectForm.actions.cancel")}
        </Button>
        <Button variant="danger" onClick={onRequestRemoval} type="button" disabled={isLoading}>
          {t("projectForm.actions.delete")}
        </Button>
      </div>
    </div>
  ) : (
    <div className="border-t surface-base-stroke pt-3 grid grid-cols-[2fr_3fr] gap-3">
      <Button variant="secondary" onClick={onClose} type="button" disabled={isLoading}>
        {t("projectForm.actions.cancel")}
      </Button>
      <Button variant="primary" type="submit" disabled={isSubmitDisabled}>
        {isLoading
          ? t("projectForm.actions.submitting")
          : mode === "create"
            ? t("projectForm.actions.create")
            : t("projectForm.actions.save")}
      </Button>
    </div>
  );

  return (
    <>
      <form onSubmit={handleSubmit}>
        <ModalFormSplit
          icon={Briefcase}
          iconBgClassName="background-brand-fill"
          secondIcon={WalletMoney}
          secondIconBgClassName="background-info-fill"
          title={
            mode === "create"
              ? t("projectForm.titleCreate")
              : project?.is_internal
                ? t("projectForm.titleEditInternal")
                : t("projectForm.titleEditExternal")
          }
          secondaryTitle={t("projectForm.secondaryTitle")}
          onClose={onClose}
          footer={footer}
          secondaryContent={rightPanel}>
          {leftPanel}
        </ModalFormSplit>
      </form>

      {errorPrompt && (
        <Prompt
          onClose={handleCloseErrorPrompt}
          title={errorPrompt.title}
          text={errorPrompt.text}
          variant="error"
          namespace="ProjectsPage"
        />
      )}

      {confirmRemoveMemberId !== null && (
        <PromptForm
          onClose={cancelRemoveMember}
          onConfirm={confirmRemoveMember}
          title={t("members.confirmRemoveTitle")}
          text={t("members.confirmRemoveText")}
          variant="error"
          isLoading={removingMemberId !== null}
          confirmText={t("members.confirmRemove")}
          cancelText={t("confirm.cancel")}
          namespace="ProjectsPage"
        />
      )}
    </>
  );
}
